'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';

interface Article {
  slug: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  date: string;
  dateDisplay: string;
}

interface ViewData {
  slug: string;
  view_count: number;
}

interface CategoryDef {
  id: string;
  name: string;
  color: string;
}

// Tailwind trenger literale klassenavn — mapper fargenavn fra CATEGORIES_META i lib/seo.ts
const COLOR_CLASSES: Record<string, string> = {
  orange: 'bg-orange-100 text-orange-700',
  green: 'bg-green-100 text-green-700',
  violet: 'bg-violet-100 text-violet-700',
  sky: 'bg-sky-100 text-sky-700',
  teal: 'bg-teal-100 text-teal-700',
  stone: 'bg-stone-100 text-stone-700',
};

type SortBy = 'nyeste' | 'eldste' | 'mest-lest';

export function ArticleSearch({ articles, categories }: { articles: Article[]; categories: CategoryDef[] }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('alle');
  const [sortBy, setSortBy] = useState<SortBy>('nyeste');
  const [views, setViews] = useState<Record<string, number>>({});

  const catById = useMemo(() => {
    const map: Record<string, CategoryDef> = {};
    categories.forEach(c => { map[c.id] = c; });
    return map;
  }, [categories]);

  const catCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    articles.forEach(a => { counts[a.category] = (counts[a.category] || 0) + 1; });
    return counts;
  }, [articles]);

  const catName = (id: string) => catById[id]?.name || id;
  const catBadgeClass = (id: string) => COLOR_CLASSES[catById[id]?.color || ''] || 'bg-stone-100 text-stone-600';

  // Fetch view counts
  useEffect(() => {
    fetch('/api/article-views')
      .then(r => r.json())
      .then((data: ViewData[]) => {
        const map: Record<string, number> = {};
        data.forEach(d => { map[d.slug] = d.view_count; });
        setViews(map);
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let result = articles;

    // Category filter
    if (category !== 'alle') {
      result = result.filter(a => a.category === category);
    }

    // Search filter
    if (query.trim()) {
      const q = query.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        (catById[a.category]?.name || '').toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortBy === 'nyeste') {
      result = [...result].sort((a, b) => b.date.localeCompare(a.date));
    } else if (sortBy === 'eldste') {
      result = [...result].sort((a, b) => a.date.localeCompare(b.date));
    } else if (sortBy === 'mest-lest') {
      result = [...result].sort((a, b) => (views[b.slug] || 0) - (views[a.slug] || 0));
    }

    return result;
  }, [articles, query, category, sortBy, views, catById]);

  const topArticles = useMemo(() => {
    return [...articles]
      .sort((a, b) => (views[b.slug] || 0) - (views[a.slug] || 0))
      .slice(0, 5)
      .filter(a => (views[a.slug] || 0) > 0);
  }, [articles, views]);

  return (
    <div>
      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder={"Søk i " + articles.length + " artikler..."}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full border border-stone-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-stone-400"
          />
        </div>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value as SortBy)}
          className="border border-stone-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-stone-400 bg-white"
        >
          <option value="nyeste">Nyeste først</option>
          <option value="eldste">Eldste først</option>
          <option value="mest-lest">Mest lest</option>
        </select>
      </div>

      {/* Category tabs — horisontalt scrollbare på mobil */}
      <div className="flex flex-nowrap gap-2 mb-6 overflow-x-auto pb-1">
        {[{ id: 'alle', name: 'Alle', count: articles.length },
          ...categories.map(c => ({ id: c.id, name: c.name, count: catCounts[c.id] || 0 }))].map(cat => (
          <button
            key={cat.id}
            onClick={() => setCategory(cat.id)}
            className={"px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex-shrink-0 whitespace-nowrap " +
              (category === cat.id
                ? 'bg-stone-900 text-white'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200')}
          >
            {cat.name}
            <span className="ml-1.5 text-stone-400">{cat.count}</span>
          </button>
        ))}
      </div>

      {/* Most read section (only show when we have view data and no search active) */}
      {topArticles.length > 0 && !query && category === 'alle' && sortBy !== 'mest-lest' && (
        <div className="mb-8">
          <h3 className="text-sm font-semibold text-stone-900 mb-3">Mest leste artikler</h3>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {topArticles.map((a, i) => (
              <Link key={a.slug} href={`/ressurser/artikler/${a.slug}`}
                className="flex-shrink-0 w-52 bg-white border border-stone-200 rounded-xl p-3 hover:shadow-sm transition-shadow group">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold text-stone-300">#{i + 1}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${catBadgeClass(a.category)}`}>
                    {catName(a.category).split(' ')[0]}
                  </span>
                </div>
                <div className="text-xs font-medium text-stone-900 group-hover:text-stone-600 line-clamp-2">{a.title}</div>
                <div className="text-xs text-stone-400 mt-1">{views[a.slug] || 0} visninger</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Results count */}
      <div className="text-xs text-stone-400 mb-3">
        {filtered.length} {filtered.length === 1 ? 'artikkel' : 'artikler'}
        {query && ` for «${query}»`}
      </div>

      {/* Article list */}
      <div className="space-y-3">
        {filtered.map(a => (
          <Link key={a.slug} href={`/ressurser/artikler/${a.slug}`}
            className="flex items-start gap-4 bg-white border border-stone-200 rounded-xl p-4 hover:shadow-sm transition-shadow group">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catBadgeClass(a.category)}`}>
                  {catName(a.category)}
                </span>
                <span className="text-xs text-stone-400">{a.readTime} lesetid</span>
              </div>
              <div className="font-medium text-stone-900 text-sm group-hover:text-stone-600 transition-colors">{a.title}</div>
              <div className="text-xs text-stone-500 mt-1 line-clamp-1">{a.description}</div>
              <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                <span>{a.dateDisplay}</span>
                {views[a.slug] > 0 && <span>{views[a.slug]} visninger</span>}
              </div>
            </div>
            <span className="text-stone-400 group-hover:text-stone-700 transition-colors text-sm mt-3">&rarr;</span>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-10 text-stone-400 text-sm">
            Ingen artikler funnet {query ? `for «${query}»` : 'i denne kategorien'}.
          </div>
        )}
      </div>
    </div>
  );
}
